import React, { useCallback, useEffect, useMemo, useState } from "react";

import { PhotoGetModel2, ProductsPhotosGetModel } from "@inbound/api";
import CloseIcon from "@mui/icons-material/Close";
import {
  Dialog,
  IconButton,
  LayoutHerodias,
  LayoutJay,
  Typography,
  useNotification,
} from "@lux/ds";
import Image, { ImagesCarousel } from "@lux/ds/Image";
import LoadingSpinner from "@lux/ds/LoadingSpinner";
import {
  GalleryContainer,
  StyledHeader,
  StyledJay,
  StyledLoaderContainer,
  StyledSliderContainer,
  StyledThumbnailContainer,
  StyledThumbnailItem,
} from "./galleryModal.styles";
import useApi from "@@hooks/useApi";
import useContent from "@@hooks/useContent";

interface IItemPhoto extends ProductsPhotosGetModel {
  label: string;
}
interface IGalleryModalProps {
  isGalleryInPage?: boolean;
  itemId: string;
  name?: string;
  onClose?: () => void;
}

const GalleryModal: React.FC<IGalleryModalProps> = ({
  isGalleryInPage,
  itemId,
  name,
  onClose,
}) => {
  const api = useApi();
  const { enqueueNotification } = useNotification();
  const {
    content: { common, itemDetails: commonContent },
  } = useContent();

  const [isFetching, setIsFetching] = useState<boolean>(true);
  const [photos, setPhotos] = useState<IItemPhoto[]>([]);
  const [selectedType, setSelectedType] = useState<string>("All");

  const selectedPhotos = useMemo(
    () => photos.find(({ type }) => type === selectedType)?.photos || [],
    [photos, selectedType],
  );

  const fetchPhotos = useCallback(async () => {
    try {
      setIsFetching(true);
      const data = await api.photos_GetPhotosByItemId(itemId);

      const allPhotos = {
        label: common.all,
        photos: data.map(({ photos }) => photos as PhotoGetModel2[]).flat(),
        type: "All",
      };
      const itemPhotos = data.map((item) => ({
        ...item,
        label: item.type as string,
      }));

      setPhotos([allPhotos, ...itemPhotos]);
    } catch {
      enqueueNotification(
        { title: commonContent.errorGetPhotos },
        { variant: "error", autoHideDuration: 4000 },
      );
    } finally {
      setIsFetching(false);
    }
  }, [api, common, commonContent, enqueueNotification, itemId, setPhotos]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        const currentIndex = photos.findIndex(
          (item) => item.type === selectedType,
        );

        if (currentIndex !== -1) {
          const nextIndex =
            (event.key === "ArrowDown"
              ? currentIndex + 1
              : currentIndex + photos.length - 1) % photos.length;

          setSelectedType(photos[nextIndex].type as string);
        }
      }
    },
    [photos, selectedType, setSelectedType],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);

    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    if (itemId) {
      fetchPhotos();
    }
  }, [fetchPhotos, itemId]);

  return isGalleryInPage ? (
    <GalleryContainer gap='none'>
      <StyledThumbnailContainer scroll='scroll'>
        {isFetching ? (
          <LoadingSpinner />
        ) : (
          photos.map((item) => (
            <StyledThumbnailItem
              key={item.type}
              onClick={() => setSelectedType(item.type as string)}
              $selected={item.type === selectedType}
            >
              <Image
                height='auto'
                src={item.photos?.[0]?.url || ""}
                width='auto'
              />
              <LayoutJay gap='none'>
                <Typography variant='caption' weight='bold'>
                  {item.label}
                </Typography>
                <Typography variant='caption'>{`${item.photos?.length || 0} ${common.images} `}</Typography>
              </LayoutJay>
            </StyledThumbnailItem>
          ))
        )}
      </StyledThumbnailContainer>

      <StyledSliderContainer>
        {isFetching ? (
          <StyledLoaderContainer>
            <LoadingSpinner />
          </StyledLoaderContainer>
        ) : (
          <ImagesCarousel
            images={selectedPhotos.map((photo) => photo.url || "")}
            hideMiniatures
          />
        )}
      </StyledSliderContainer>
    </GalleryContainer>
  ) : (
    <Dialog onClose={onClose} open>
      <StyledJay gap='none' scroll='hidden'>
        <StyledHeader>
          <Typography variant='h6' weight='bold'>
            {name}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </StyledHeader>
        <LayoutHerodias gap='none' sx={{ overflow: "hidden" }}>
          <StyledThumbnailContainer scroll='scroll'>
            {isFetching ? (
              <LoadingSpinner />
            ) : (
              photos.map((item) => (
                <StyledThumbnailItem
                  key={item.type}
                  onClick={() => setSelectedType(item.type as string)}
                  $selected={item.type === selectedType}
                >
                  <Image
                    height='auto'
                    src={item.photos?.[0]?.url || ""}
                    width='auto'
                  />
                  <LayoutJay gap='none'>
                    <Typography variant='caption' weight='bold'>
                      {item.label}
                    </Typography>
                    <Typography variant='caption'>{`${item.photos?.length || 0} ${common.images}'`}</Typography>
                  </LayoutJay>
                </StyledThumbnailItem>
              ))
            )}
          </StyledThumbnailContainer>

          <StyledSliderContainer>
            {isFetching ? (
              <StyledLoaderContainer>
                <LoadingSpinner />
              </StyledLoaderContainer>
            ) : (
              <ImagesCarousel
                images={selectedPhotos.map((photo) => photo.url || "")}
              />
            )}
          </StyledSliderContainer>
        </LayoutHerodias>
      </StyledJay>
    </Dialog>
  );
};

export default GalleryModal;
